import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useProvider } from 'src/state/provider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useHeaderMenu } from 'src/state/headerMenu';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { styled } from '@uhg-abyss/web/tools/styled';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import {
  useProviderPreferenceQuery,
  formatVariables,
} from '@sde/widgets-core/src/common/hooks/useProviderPreferenceQuery';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { Text } from '@uhg-abyss/web/ui/Text';
import { usercache } from '@sde/widgets-core/src/redux/tools/usercache';
import { storage } from '@uhg-abyss/web/tools/storage';
import { ProviderSelectWidget } from './ProviderSelectWidget';

const StrongText = styled('strong', {
  fontSize: '14px',
});

const ThinBorderButton = styled(Button, {
  '&.abyss-button-root': {
    borderWidth: '1px',
  },
});

const NoOverflowPopover = styled(Popover, {
  '&.abyss-popover-content-container': {
    overflow: 'hidden !important',
  },
});

export const ProviderSelect = ({ setDigitalSecurityError }) => {
  const { user } = useUser.state();
  const { updateUser } = useUser.actions();
  const [providerNotSaved, setProviderNotSaved] = useState(false);
  const { provider } = useProvider.state();
  const { setProvider } = useProvider.actions();
  const { isProviderOpen } = useHeaderMenu.state();
  const { toggleProvider } = useHeaderMenu.actions();
  const permissionsStorageKey = 'provider-permissions';

  const [, getProviderPreference] = useProviderPreferenceQuery({
    onCompleted: (response) => {
      setProvider(response.provider);
      if (
        response?.permissions?.length > 0 ||
        response?.permissionSubCode?.length > 0 ||
        response?.permissionCode?.length > 0 ||
        response?.allPermission
      ) {
        updateUser({
          permissions: response.permissions,
          privileges: response.organizations,
          allPermission: response.allPermission,
          permissionSubCode: response.permissionSubCode || [],
          permissionCode: response.permissionCode || [],
          provider: response.provider,
        });
      }
    },
    onError: (err) => {
      const cachedProvider = usercache.get('provider');
      if (cachedProvider) {
        setProvider({});
      }
      storage.local.remove(permissionsStorageKey);
      const { code, message } = err;
      setDigitalSecurityError({
        code,
        message,
        isDigitalSecurityError: true,
        isProviderPreferenceError: true,
      });
      setProviderNotSaved(true);
    },
  });

  useEffect(() => {
    if (user.uuid) {
      getProviderPreference({
        variables: formatVariables(user),
      });
    }
  }, [user.uuid]);

  return (
    <NoOverflowPopover
      showClose={false}
      width={550}
      position="bottom"
      align="end"
      open={isProviderOpen}
      onOpenChange={toggleProvider}
      content={
        <ProviderSelectWidget
          provider={provider}
          setProvider={setProvider}
          isDropdownHidden={!isProviderOpen}
          toggleProvider={toggleProvider}
          setProviderNotSaved={setProviderNotSaved}
          setDigitalSecurityError={setDigitalSecurityError}
        />
      }
    >
      <Text size={14} fontWeight={600}>
        Provider{' '}
      </Text>
      <ThinBorderButton
        size="$sm"
        variant="outline"
        after={
          !provider.servicingProviderName ? (
            <React.Fragment>
              <LoadingSpinner
                size="$sm"
                isLoading={!provider.servicingProviderName}
                ariaLoadingLabel="Loading Providers"
              />
              <IconMaterial icon="keyboard_arrow_down" color="$primary1" />
            </React.Fragment>
          ) : (
            <IconMaterial icon="keyboard_arrow_down" color="$primary1" />
          )
        }
      >
        <Choose>
          <When condition={providerNotSaved}>Select...</When>
          <Otherwise>
            <StrongText>{provider.servicingProviderName}</StrongText>
          </Otherwise>
        </Choose>
      </ThinBorderButton>
    </NoOverflowPopover>
  );
};

ProviderSelect.propTypes = {
  setDigitalSecurityError: PropTypes.func.isRequired,
};
